import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { TagsSection } from "@/components/TagsSection";
import { adminStationsApi, adminTagsApi, adminUsersApi, devicesApi, stagesApi } from "@/lib/api";
import { displayName, formatDateTime } from "@/lib/format";
import { useRoles } from "@/lib/roles";
import type { AdminUserRead, OperatorRole, UUID } from "@/lib/types";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";
const MONO_INPUT_CLS = `${INPUT_CLS} font-mono`;

export default function UserDetail() {
  const { id } = useParams<{ id: UUID }>();
  return (
    <RequirePermission codes="user.manage">
      {id ? <UserDetailInner id={id} /> : <ErrorState error={new Error("No user id")} />}
    </RequirePermission>
  );
}

function UserDetailInner({ id }: { id: UUID }) {
  const { t } = useTranslation();
  const userQ = useQuery({
    queryKey: ["admin", "users", id],
    queryFn: () => adminUsersApi.get(id),
  });

  if (userQ.isLoading) return <LoadingState />;
  if (userQ.isError) return <ErrorState error={userQ.error} />;
  const user = userQ.data!;

  return (
    <>
      <PageHeader
        eyebrow={`admin · user · ${user.role}`}
        title={user.name}
        description={user.login ? `login: ${user.login}` : t("userDetail.nfcPinOnly")}
        actions={
          <Link to="/admin/users" className="btn">{t("userDetail.allUsers")}</Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <IdentitySection user={user} />
        <StatusSection user={user} />
        <CredentialsSection user={user} />
        <StationsSection user={user} />
        <StagesSection user={user} />
        <DevicesSection user={user} />
        <TagsSection
          targetType="operator"
          targetId={user.id}
          hint={t("userDetail.tagsHint")}
        />
      </div>
    </>
  );
}

// --- Stations (M:N через operator_station_assignments) ---------------------

function StationsSection({ user }: { user: AdminUserRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const stationsQ = useQuery({
    queryKey: ["admin", "stations", "all", "for-user-detail"],
    queryFn: () => adminStationsApi.list({ page_size: 200, is_active: true }),
    staleTime: 60_000,
  });
  const assignedQ = useQuery({
    queryKey: ["admin", "users", user.id, "stations"],
    queryFn: () => adminUsersApi.listStations(user.id),
  });

  const [selected, setSelected] = useState<number[]>([]);
  useEffect(() => {
    if (assignedQ.data) setSelected(assignedQ.data.map((s) => s.id));
  }, [assignedQ.data?.map((s) => s.id).join(",")]);

  const m = useMutation({
    mutationFn: () => adminUsersApi.setStations(user.id, { station_ids: selected }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users", user.id, "stations"] });
    },
  });

  return (
    <Section title={t("userDetail.stationsTitle")}>
      <p className="text-xs text-text-muted">
        {t("userDetail.stationsHint")}
      </p>
      {stationsQ.isLoading || assignedQ.isLoading ? (
        <LoadingState />
      ) : stationsQ.isError ? (
        <ErrorState error={stationsQ.error} />
      ) : (
        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
          {stationsQ.data!.items.length === 0 && (
            <p className="text-text-muted text-sm">{t("userDetail.noStations")}</p>
          )}
          {stationsQ.data!.items.map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(s.id)}
                onChange={(e) => {
                  setSelected((cur) =>
                    e.target.checked
                      ? [...cur, s.id].sort((a, b) => a - b)
                      : cur.filter((x) => x !== s.id),
                  );
                }}
              />
              <span className="font-mono text-text-muted tabular-nums">{s.id}</span>
              <span className="font-mono">{s.code}</span>
              <span className="text-text-secondary text-xs truncate">{s.name}</span>
            </label>
          ))}
        </div>
      )}
      <button
        className="btn btn-primary"
        disabled={m.isPending}
        onClick={() => m.mutate()}
      >
        {m.isPending ? t("common.saving") : t("userDetail.saveBindings")}
      </button>
      {m.isError && <ErrorState error={m.error} />}
    </Section>
  );
}

// --- Identity ----------------------------------------------------------------

function IdentitySection({ user }: { user: AdminUserRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(user.name);
  const [login, setLogin] = useState(user.login ?? "");
  const [role, setRole] = useState<OperatorRole>(user.role);
  const roles = useRoles();
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setName(user.name); setLogin(user.login ?? ""); setRole(user.role);
  }, [user.id, user.name, user.login, user.role]);

  // Тянем активные метки этого юзера — выводим NFC read-only из tags.
  // Это убирает дубликат: раньше nfc_tag жил и на operator-row, и в
  // tags-таблице одновременно.
  const userTags = useQuery({
    queryKey: ["admin", "users", user.id, "tags"],
    queryFn: () => adminTagsApi.forUser(user.id),
    staleTime: 30_000,
  });
  const activeNfc = (userTags.data ?? []).find(
    (t) => t.kind === "NFC" && t.is_active,
  );

  const m = useMutation({
    mutationFn: () =>
      adminUsersApi.update(user.id, {
        name,
        login: login || null,
        role,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", user.id] }),
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  return (
    <Section title={t("userDetail.identity")}>
      <Field label={t("userDetail.fullName")}>
        <input className={INPUT_CLS} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Login">
        <input className={MONO_INPUT_CLS} value={login} onChange={(e) => setLogin(e.target.value)} />
      </Field>
      <Field label={t("userDetail.nfcTag")}>
        <div className="flex items-center gap-2">
          {activeNfc ? (
            <span className="font-mono px-3 py-2 rounded-md bg-surface-alt border border-border flex-1 min-w-0 truncate">
              {activeNfc.value}
            </span>
          ) : (
            <span className="font-mono px-3 py-2 rounded-md bg-surface-alt border border-border text-text-muted flex-1">
              {t("userDetail.notBound")}
            </span>
          )}
          <a
            href="/admin/tags"
            className="btn text-xs whitespace-nowrap"
            title={t("userDetail.tagsManageTitle")}
          >
            {activeNfc ? t("userDetail.rebind") : t("userDetail.bind")} →
          </a>
        </div>
      </Field>
      <Field label={t("userDetail.role")}>
        <select
          className={MONO_INPUT_CLS}
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          {roles.assignable.map((r) => (
            <option key={r.role} value={r.role}>{r.name}</option>
          ))}
        </select>
      </Field>
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      <button
        className="btn btn-primary"
        disabled={m.isPending}
        onClick={() => { setErr(null); m.mutate(); }}
      >
        {m.isPending ? t("common.saving") : t("common.save")}
      </button>
    </Section>
  );
}

// --- Status / activation ----------------------------------------------------

function StatusSection({ user }: { user: AdminUserRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      user.is_active ? adminUsersApi.deactivate(user.id) : adminUsersApi.activate(user.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", user.id] }),
  });

  return (
    <Section title={t("userDetail.statusTitle")}>
      <Kv k={t("userDetail.statusKv")} v={
        <span className={`pill ${user.is_active ? "pill-ok" : ""}`}>
          {user.is_active ? t("users.enabled") : t("users.disabled")}
        </span>
      } />
      <Kv k="ID" v={<span className="font-mono text-xs">{user.id}</span>} />
      <Kv k={t("common.created")} v={user.created_at ? formatDateTime(user.created_at) : "—"} />
      <Kv k={t("userDetail.lastLogin")} v={user.last_login_at ? formatDateTime(user.last_login_at) : "—"} />
      <Kv k={t("userDetail.passwordChange")} v={user.must_change_password ? t("userDetail.required") : "—"} />
      {toggle.isError && <ErrorState error={toggle.error} />}
      <button
        className={`btn ${user.is_active ? "btn-danger" : "btn-primary"}`}
        onClick={() => {
          if (user.is_active && !window.confirm(t("userDetail.confirmDeactivate"))) return;
          toggle.mutate();
        }}
        disabled={toggle.isPending}
      >
        {user.is_active ? t("userDetail.deactivate") : t("userDetail.activate")}
      </button>
    </Section>
  );
}

// --- Credentials reset -------------------------------------------------------

function CredentialsSection({ user }: { user: AdminUserRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [pwd, setPwd] = useState("");
  const [pin, setPin] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const pwdM = useMutation({
    mutationFn: () => adminUsersApi.resetPassword(user.id, { new_password: pwd, force_change: true }),
    onSuccess: () => { setMsg(t("userDetail.passwordReset")); setPwd(""); qc.invalidateQueries({ queryKey: ["admin", "users", user.id] }); },
    onError: (e) => setMsg(e instanceof Error ? e.message : t("common.error")),
  });
  const pinM = useMutation({
    mutationFn: () => adminUsersApi.resetPin(user.id, { new_pin: pin }),
    onSuccess: () => { setMsg(t("userDetail.pinSaved")); setPin(""); qc.invalidateQueries({ queryKey: ["admin", "users", user.id] }); },
    onError: (e) => setMsg(e instanceof Error ? e.message : t("common.error")),
  });
  const badgeM = useMutation({
    mutationFn: (rotate: boolean) => adminUsersApi.issueQrBadge(user.id, { rotate }),
    onSuccess: (res) => {
      setMsg(t("userDetail.qrBadgeIssued", { count: res.revoked_count }));
      qc.invalidateQueries({ queryKey: ["admin", "users", user.id] });
    },
    onError: (e) => setMsg(e instanceof Error ? e.message : t("common.error")),
  });

  return (
    <Section title={t("userDetail.credentialsTitle")}>
      <p className="text-xs text-text-muted">
        {t("userDetail.credentialsHint")}
      </p>
      <Field label={t("userDetail.newPassword")}>
        <div className="flex gap-2">
          <input
            className={INPUT_CLS}
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder={t("userDetail.min8")}
            autoComplete="new-password"
          />
          <button
            className="btn"
            disabled={pwd.length < 8 || pwdM.isPending}
            onClick={() => { setMsg(null); pwdM.mutate(); }}
          >{t("common.reset")}</button>
        </div>
      </Field>
      <Field label="PIN">
        <div className="flex gap-2">
          <input
            className={MONO_INPUT_CLS}
            value={pin}
            inputMode="numeric"
            onChange={(e) => setPin(e.target.value)}
            placeholder={t("userDetail.pinPlaceholder")}
          />
          <button
            className="btn"
            disabled={pin.length < 4 || pinM.isPending}
            onClick={() => { setMsg(null); pinM.mutate(); }}
          >{t("common.save")}</button>
        </div>
      </Field>
      <p className="text-xs text-text-muted">
        {t("userDetail.nfcNote1")} <a className="text-accent-strong" href="/admin/tags">{t("userDetail.tagsPage")}</a>
        {" "}{t("userDetail.nfcNote2")} <code className="font-mono">/m/bind</code>.
      </p>
      <Field label={t("userDetail.qrBadge")}>
        <div className="flex gap-2">
          <button
            className="btn"
            disabled={badgeM.isPending}
            onClick={() => { setMsg(null); badgeM.mutate(false); }}
          >{t("userDetail.qrBadgeIssue")}</button>
          <button
            className="btn"
            disabled={badgeM.isPending}
            onClick={() => {
              if (window.confirm(t("userDetail.qrBadgeRotateConfirm"))) {
                setMsg(null);
                badgeM.mutate(true);
              }
            }}
          >{t("userDetail.qrBadgeRotate")}</button>
        </div>
      </Field>
      <p className="text-xs text-text-muted">{t("userDetail.qrBadgeHint")}</p>
      {msg && <p className="text-text-secondary text-sm">{msg}</p>}
    </Section>
  );
}

// --- Stage scope -------------------------------------------------------------

function StagesSection({ user }: { user: AdminUserRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const stagesQ = useQuery({
    queryKey: ["stages", "all"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
  });
  const [selected, setSelected] = useState<number[]>(user.allowed_stage_ids);
  useEffect(() => setSelected(user.allowed_stage_ids), [user.id, user.allowed_stage_ids]);

  const m = useMutation({
    mutationFn: () => adminUsersApi.setStages(user.id, { allowed_stage_ids: selected }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", user.id] }),
  });

  return (
    <Section title={t("userDetail.stagesTitle")}>
      <p className="text-xs text-text-muted">
        {t("userDetail.stagesHint")}
      </p>
      {stagesQ.isLoading ? (
        <LoadingState />
      ) : stagesQ.isError ? (
        <ErrorState error={stagesQ.error} />
      ) : (
        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
          {stagesQ.data!.items.map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(s.id)}
                onChange={(e) => {
                  setSelected((cur) =>
                    e.target.checked ? [...cur, s.id].sort((a, b) => a - b) : cur.filter((x) => x !== s.id),
                  );
                }}
              />
              <span className="font-mono text-text-muted tabular-nums">{s.id}</span>
              <span className="font-mono">{s.code}</span>
              <span className="text-text-secondary">{displayName(s)}</span>
            </label>
          ))}
        </div>
      )}
      <button
        className="btn btn-primary"
        disabled={m.isPending}
        onClick={() => m.mutate()}
      >
        {m.isPending ? t("common.saving") : t("userDetail.saveBindings")}
      </button>
      {m.isError && <ErrorState error={m.error} />}
    </Section>
  );
}

// --- Device scope ------------------------------------------------------------

function DevicesSection({ user }: { user: AdminUserRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const devicesQ = useQuery({
    queryKey: ["devices", "all"],
    queryFn: () => devicesApi.list({ page_size: 200 }),
  });
  const [selected, setSelected] = useState<UUID[]>(user.allowed_device_ids);
  useEffect(() => setSelected(user.allowed_device_ids), [user.id, user.allowed_device_ids]);

  const m = useMutation({
    mutationFn: () => adminUsersApi.setDevices(user.id, { allowed_device_ids: selected }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", user.id] }),
  });

  return (
    <Section title={t("userDetail.devicesTitle")}>
      <p className="text-xs text-text-muted">
        {t("userDetail.devicesHint")}
      </p>
      {devicesQ.isLoading ? (
        <LoadingState />
      ) : devicesQ.isError ? (
        <ErrorState error={devicesQ.error} />
      ) : (
        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
          {devicesQ.data!.items.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(d.id)}
                onChange={(e) => {
                  setSelected((cur) =>
                    e.target.checked ? [...cur, d.id] : cur.filter((x) => x !== d.id),
                  );
                }}
              />
              <span className="font-mono">{d.code}</span>
              <span className="text-text-secondary text-xs">{d.type}</span>
              {d.station_id && <span className="text-text-muted text-xs">@ {d.station_id}</span>}
            </label>
          ))}
        </div>
      )}
      <button
        className="btn btn-primary"
        disabled={m.isPending}
        onClick={() => m.mutate()}
      >
        {m.isPending ? t("common.saving") : t("userDetail.saveBindings")}
      </button>
      {m.isError && <ErrorState error={m.error} />}
    </Section>
  );
}

// --- Layout helpers ----------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card p-5 space-y-4">
      <h2 className="text-lg">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Kv({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-text-muted text-xs uppercase tracking-wider font-mono">{k}</span>
      <span className="text-text-primary">{v}</span>
    </div>
  );
}
